<template>
    <BaseField
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        data-spec-class="field-tiptap"
    >
        <div
            :class="[
                'relative rounded-md transition-ease',
                {
                    [`border border-divider shadow-xs ${focusWithinRingClasses} focus-within:ring-brand`]:
                        hasWrapper && theme === Theme.Normal,
                    [`border border-danger shadow-xs ${focusWithinRingClasses} focus-within:ring-danger`]:
                        hasWrapper && theme === Theme.Danger,
                    'border border-secondary--inverse shadow-xs':
                        hasWrapper && theme === Theme.Disabled,
                },
            ]"
        >
            <div
                v-if="editor && !isDisabled"
                class="flex items-center gap-1 border-b border-divider px-2 py-1"
                data-spec-class="field-tiptap__toolbar"
            >
                <button
                    type="button"
                    :class="[
                        'rounded-sm p-1.5 text-secondary hover:bg-surface--light hover:text-primary',
                        editor.isActive('bold') ? 'bg-surface--light text-primary' : '',
                    ]"
                    :disabled="!editor.can().chain().focus().toggleBold().run()"
                    title="Bold"
                    data-spec-class="field-tiptap__toolbar-bold"
                    @click="editor.chain().focus().toggleBold().run()"
                >
                    <BoldIcon class="h-4 w-4" />
                </button>
                <button
                    type="button"
                    :class="[
                        'rounded-sm p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
                        editor.isActive('italic')
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                            : '',
                    ]"
                    :disabled="!editor.can().chain().focus().toggleItalic().run()"
                    title="Italic"
                    data-spec-class="field-tiptap__toolbar-italic"
                    @click="editor.chain().focus().toggleItalic().run()"
                >
                    <ItalicIcon class="h-4 w-4" />
                </button>
                <div class="mx-0.5 h-4 w-px bg-tertiary"></div>
                <button
                    type="button"
                    :class="[
                        'rounded-sm px-2 py-1 text-xs font-medium text-secondary hover:bg-surface--light hover:text-primary',
                        editor.isActive('heading', { level: 2 })
                            ? 'bg-surface--light text-primary'
                            : '',
                    ]"
                    title="Heading"
                    data-spec-class="field-tiptap__toolbar-heading"
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                >
                    H2
                </button>
                <button
                    type="button"
                    :class="[
                        'rounded-sm p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
                        editor.isActive('bulletList')
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                            : '',
                    ]"
                    title="Bullet List"
                    data-spec-class="field-tiptap__toolbar-bullet-list"
                    @click="editor.chain().focus().toggleBulletList().run()"
                >
                    <ListBulletIcon class="h-4 w-4" />
                </button>
                <div class="mx-0.5 h-4 w-px bg-tertiary"></div>
                <button
                    type="button"
                    :class="[
                        'rounded-sm p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
                        editor.isActive('blockquote')
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                            : '',
                    ]"
                    title="Quote"
                    data-spec-class="field-tiptap__toolbar-blockquote"
                    @click="editor.chain().focus().toggleBlockquote().run()"
                >
                    <ChatBubbleLeftRightIcon class="h-4 w-4" />
                </button>
                <button
                    type="button"
                    :class="[
                        'rounded-sm p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
                        editor.isActive('code')
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                            : '',
                    ]"
                    title="Code"
                    data-spec-class="field-tiptap__toolbar-code"
                    @click="editor.chain().focus().toggleCode().run()"
                >
                    <CodeBracketIcon class="h-4 w-4" />
                </button>
            </div>
            <div :class="[classObject, 'min-h-[120px] resize-y overflow-auto']">
                <EditorContent
                    :editor="editor"
                    :data-spec-class="dataSpecClass"
                    class="editor-content prose prose-sm"
                />
            </div>

            <div
                v-if="errorMessagesTotal.length || !!$slots.addOn"
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            >
                <ExclamationCircleIcon
                    v-if="errorMessagesTotal.length"
                    class="h-5 w-5 text-danger"
                    aria-hidden="true"
                />

                <span
                    v-if="!!$slots.addOn"
                    class="ml-1 text-tertiary sm:text-sm"
                >
                    <slot name="addOn" />
                </span>
            </div>
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { withDefaults, computed, onBeforeUnmount, watch } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import {
    ExclamationCircleIcon,
    BoldIcon,
    ItalicIcon,
    ListBulletIcon,
    ChatBubbleLeftRightIcon,
    CodeBracketIcon,
} from '@heroicons/vue/20/solid';
import { Theme } from '@/types';
import BaseField from './BaseField.vue';
import { useField } from '@/composables/field';

const { focusWithinRingClasses } = useField();

interface Props {
    name: string;
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    wrapperClassAddition?: string;
    customThemeBase?: string;
    customThemeNormal?: string;
    dataSpecClass?: string;
    hasAutoDirty?: boolean;
    hasWrapper?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    modelValue: undefined,
    vuelidateField: undefined,
    errorMessages: () => [],
    wrapperClassAddition: undefined,
    customThemeBase: undefined,
    customThemeNormal: undefined,
    dataSpecClass: 'field-tiptap__editor',
    hasAutoDirty: true,
    hasWrapper: true,
});

const emits = defineEmits(['update:modelValue', 'keydown']);

let isInternalUpdate = false;

const editor = useEditor({
    content: props.modelValue || '',
    extensions: [StarterKit],
    editable: !props.isDisabled,
    onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const normalizedValue = editor.isEmpty ? '' : html;
        isInternalUpdate = true;
        emits('update:modelValue', normalizedValue);
        if (props.hasAutoDirty && props.vuelidateField) {
            props.vuelidateField.$touch();
        }
    },
    onBlur: () => {
        if (props.hasAutoDirty && props.vuelidateField) {
            props.vuelidateField.$touch();
        }
    },
    editorProps: {
        attributes: {
            class: 'prose prose-sm max-w-none focus:outline-hidden min-h-[120px] py-1.5 px-3 text-primary placeholder-muted sm:text-sm/6',
            'data-placeholder': props.placeholder || '',
        },
        handleKeyDown: (view, event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                emits('keydown', event);
                return true;
            }
            return false;
        },
    },
});

watch(
    () => props.modelValue,
    (value) => {
        if (isInternalUpdate) {
            isInternalUpdate = false;
            return;
        }
        if (!editor.value) return;
        const isEmpty = !value || value.trim() === '' || value === '<p></p>';
        if (editor.value.isFocused && !isEmpty) return;
        const currentContent = editor.value.getHTML();
        const newContent = value || '<p></p>';
        if (currentContent !== newContent) {
            editor.value.commands.setContent(newContent);
        }
    }
);

watch(
    () => props.isDisabled,
    (value) => {
        editor.value?.setEditable(!value);
    }
);

onBeforeUnmount(() => {
    editor.value?.destroy();
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }

    return Theme.Normal;
});

const themeBase = computed(() => {
    const defaultThemeBase = 'block w-full';
    return typeof props.customThemeBase === 'string' ? props.customThemeBase : defaultThemeBase;
});

const themeNormal = computed(() => {
    const defaultThemeNormal = '';
    return typeof props.customThemeNormal === 'string'
        ? props.customThemeNormal
        : defaultThemeNormal;
});

const classObject = computed(() => ({
    [themeBase.value]: true,
    [themeNormal.value]: theme.value === Theme.Normal,
}));
</script>

<style scoped src="../../css/editor-content.css"></style>
